var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { isComponentVariableMutable, } from "./ComponentVariableMutable";
import { useComponent } from "../../component/composables";
import { until } from "@vueuse/core";
import { ref, shallowReactive } from "vue";
import { campaignComponentUpdatedEvent } from "../../event-bus/campaign-component";
import { builtComponentUpdatedEvent } from "../../event-bus/component-built";
import { debounce } from "underscore";
import { createComponentSettingMutable } from "./factory/createComponentSettingMutable";
export class ComponentMutable {
    constructor(type, componentMutableKey, component) {
        this.settings = shallowReactive({});
        this.getName = () => {
            return this.name.value;
        };
        this.setName = (name) => {
            this.name.value = name;
        };
        this.getDisplayName = () => {
            return this.displayName.value;
        };
        this.getDescription = () => {
            return this.description.value;
        };
        this.setDisplayName = (displayName) => {
            if (displayName === "") {
                this.displayName.value = null;
                return;
            }
            this.displayName.value = displayName;
        };
        this.setDescription = (description) => {
            if (description === "") {
                this.description.value = null;
                return;
            }
            this.description.value = description;
        };
        this.pull = () => __awaiter(this, void 0, void 0, function* () {
            const { component } = useComponent(this.component_id);
            yield until(component).not.toBeUndefined();
            if (!component.value) {
                return;
            }
            this.lastPulledName.value = component.value.name;
            this.name.value = this.lastPulledName.value;
            this.lastPulledDisplayName.value = component.value.display_name;
            this.displayName.value = this.lastPulledDisplayName.value;
            this.lastPulledDescription.value = component.value.description;
            this.description.value = this.lastPulledDescription.value;
            const settingNames = component.value
                .listSettings()
                .map((setting) => setting.name);
            const currentSettingNames = Object.keys(this.settings);
            // REMOVE SETTINGS THAT NO LONGER EXIST
            const settingsToRemove = currentSettingNames.filter((variableName) => !settingNames.includes(variableName));
            settingsToRemove.forEach((variableName) => {
                delete this.settings[variableName];
            });
            // ADD NEW VARIABLES
            const settingsToAdd = settingNames.filter((variableName) => !currentSettingNames.includes(variableName));
            settingsToAdd.forEach((name) => {
                var _a;
                const setting = (_a = component.value) === null || _a === void 0 ? void 0 : _a.findSetting(name);
                if (!setting) {
                    return;
                }
                this.settings[name] = createComponentSettingMutable(this.componentMutableKey, this.type, setting);
            });
            // PULL VARIABLES
            const promises = Object.values(this.settings).map((setting) => setting.pull());
            yield Promise.all(promises);
        });
        this.getSetting = (name) => {
            return this.settings[name];
        };
        this.getVariable = (name) => {
            const setting = this.getSetting(name);
            if (!setting || !isComponentVariableMutable(setting)) {
                return undefined;
            }
            return setting;
        };
        this.getVariables = () => {
            const output = [];
            Object.values(this.settings).forEach((setting) => {
                if (isComponentVariableMutable(setting)) {
                    output.push(setting);
                }
            });
            return output;
        };
        this.getDirtySettings = () => {
            return Object.values(this.settings).filter((setting) => setting.isDirty());
        };
        this.getSettings = () => {
            return Object.values(this.settings);
        };
        this.areAttributesDirty = () => {
            return (this.name.value !== this.lastPulledName.value ||
                this.displayName.value !== this.lastPulledDisplayName.value ||
                this.description.value !== this.lastPulledDescription.value);
        };
        this.areSettingsDirty = () => {
            return Object.values(this.settings).some((setting) => setting.isDirty());
        };
        this.isDirty = () => {
            return this.areAttributesDirty() || this.areSettingsDirty();
        };
        this.componentMutableKey = componentMutableKey;
        this.component_id = component.id;
        this.campaign_id = component.campaign_id;
        this.type = type;
        this.tags = [...component.tags];
        this.name = ref(component.name);
        this.lastPulledName = ref(component.name);
        this.displayName = ref(component.display_name);
        this.description = ref(component.description);
        this.lastPulledDisplayName = ref(component.display_name);
        this.lastPulledDescription = ref(component.description);
        const pullDebounced = debounce(() => {
            this.pull();
        }, 1000);
        campaignComponentUpdatedEvent.on((event) => {
            if (event.componentId === this.component_id) {
                pullDebounced();
            }
        });
        builtComponentUpdatedEvent.on((event) => {
            if (event.componentId === this.component_id) {
                pullDebounced();
            }
        });
    }
}
